import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Send, Sparkles, Check } from "lucide-react";

const LANE_TO_SITUATION = {
  "selling-the-home": "sell_the_home", "senior-living-placement": "find_senior_living",
  "keep-mama-home": "keep_mom_home", "move-mama-into-my-home": "move_mama_into_my_home",
  "downsizing-estate-support": "downsize", "family-transition-roadmap": "need_roadmap", "start-here": "not_sure",
};
const SERVICE_TO_SITUATION = {
  sell_home: "sell_the_home", senior_placement: "find_senior_living", home_safety: "keep_mom_home",
  move_into_my_home: "move_mama_into_my_home", downsizing: "downsize", home_repairs: "keep_mom_home",
};
const CONTACT_OPTIONS = [
  { value: "phone", label: "Phone call" }, { value: "email", label: "Email" }, { value: "any", label: "Either is fine" },
];
const ic = "h-12 text-[16px] rounded-xl border-[#1C4358]/20 focus-visible:ring-0 bg-white";
const F = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[15px] font-semibold text-[#1C4358]">{label} {required && <span className="text-[#9C1E22]">*</span>}</Label>
    {children}
  </div>
);

export const IntakeForm = ({
  cta = "Intake Form", source = "Intake Form", campaign = null, franchise = null,
  extra = null, defaultService = "", submitLabel = "Request my free consultation",
}) => {
  const navigate = useNavigate();
  const routeLoc = useLocation();
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(1);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [helper, setHelper] = useState("");
  const [helperLoading, setHelperLoading] = useState(false);
  const trackedView = useRef(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", city: "", state_province: "",
    loved_one_name: "", preferred_contact_method: "", best_time_to_contact: "",
    main_concern: "", situation: "", urgency: "", email_consent: false,
  });
  const [answers, setAnswers] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const track = useCallback((event) => {
    publicApi.post("/public/intake/track", {
      event, page_path: routeLoc.pathname + routeLoc.search,
      situation: form.situation || null, reverse_search_topic: extra?.reverse_search_topic || null,
      franchise: franchise || null,
    }).catch(() => {});
  }, [routeLoc.pathname, routeLoc.search, form.situation, extra, franchise]);

  useEffect(() => {
    publicApi.get("/public/intake/schema", { params: franchise ? { franchise } : {} })
      .then(({ data }) => {
        setSchema(data);
        const pre = LANE_TO_SITUATION[extra?.matched_service_lane] || SERVICE_TO_SITUATION[defaultService] || "";
        if (pre) setForm((f) => ({ ...f, situation: pre }));
      })
      .catch(() => setSchema({ conditional_questions: {}, situations: [], urgency_options: [], step_titles: [] }));
    if (!trackedView.current) { trackedView.current = true; track("view"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markStarted = () => { if (!started) { setStarted(true); track("start"); } };

  const askHelper = async () => {
    setHelperLoading(true);
    try {
      const { data } = await publicApi.post("/public/intake/helper", { field_label: "Tell us what's happening", situation: form.situation || null });
      setHelper(data.helper || "");
    } catch { /* ignore */ }
    finally { setHelperLoading(false); }
  };

  const stepValid = () => {
    if (step === 1) return form.first_name.trim() && form.phone.trim() && form.city.trim() && form.state_province.trim();
    if (step === 2) return form.main_concern.trim();
    if (step === 3) return !!form.situation;
    if (step === 5) return !!form.urgency;
    return true;
  };

  const next = () => {
    markStarted();
    if (!stepValid()) { toast.error("Please fill in the highlighted fields."); return; }
    setStep((s) => Math.min(5, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.urgency) { toast.error("Please choose how soon you need help."); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form, country: "USA", cta, source, campaign,
        franchise: franchise || undefined, page_path: routeLoc.pathname + routeLoc.search,
        answers, ...(extra || {}),
      };
      const { data } = await publicApi.post("/public/intake/submit", payload);
      navigate("/thank-you", { state: { name: form.first_name, thank_you: data.thank_you } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally { setSubmitting(false); }
  };

  if (!schema) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#1C4358]" size={26} /></div>;

  const situations = schema.situations || [];
  const urgencyOptions = schema.urgency_options || [];
  const stepTitles = schema.step_titles || [];
  const condQs = (schema.conditional_questions || {})[form.situation] || [];
  const helperOverride = (schema.helper_text || {})["main_concern"];

  return (
    <div data-testid="intake-form">
      {/* progress */}
      <div className="mb-5">
        <div className="flex items-center justify-between text-[13px] font-semibold text-[#1C4358] mb-1.5">
          <span data-testid="intake-step-indicator">Step {step} of 5</span>
          <span className="text-[#4B5563] font-normal">{stepTitles[step - 1] || ""}</span>
        </div>
        <div className="h-2 rounded-full bg-[#1C4358]/10 overflow-hidden">
          <div className="h-full bg-[#9C1E22] transition-all" style={{ width: `${(step / 5) * 100}%` }} />
        </div>
      </div>

      {schema.local_intro && step === 1 && (
        <p className="mb-4 text-[14px] text-[#2F7D5A] bg-[#2F7D5A]/8 rounded-lg px-3 py-2">{schema.local_intro}</p>
      )}

      {/* STEP 1 — contact */}
      {step === 1 && (
        <div className="space-y-4" data-testid="intake-step-1">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Your name" required><Input data-testid="intake-first-name" className={ic} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="First name" /></F>
            <F label="Last name"><Input data-testid="intake-last-name" className={ic} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Optional" /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Phone" required><Input data-testid="intake-phone" type="tel" className={ic} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(816) 555-0100" /></F>
            <F label="Email"><Input data-testid="intake-email" type="email" className={ic} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Optional" /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="City" required><Input data-testid="intake-city" className={ic} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Kansas City" /></F>
            <F label="State / Province" required><Input data-testid="intake-state" className={ic} value={form.state_province} onChange={(e) => set("state_province", e.target.value)} placeholder="MO" /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Loved one's name"><Input data-testid="intake-loved-one" className={ic} value={form.loved_one_name} onChange={(e) => set("loved_one_name", e.target.value)} placeholder="Optional" /></F>
            <F label="Best way to reach you">
              <Select value={form.preferred_contact_method} onValueChange={(v) => set("preferred_contact_method", v)}>
                <SelectTrigger data-testid="intake-contact-method" className={ic}><SelectValue placeholder="Phone or email" /></SelectTrigger>
                <SelectContent>{CONTACT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
        </div>
      )}

      {/* STEP 2 — what's happening */}
      {step === 2 && (
        <div className="space-y-3" data-testid="intake-step-2">
          <F label="Tell us what's happening with your loved one" required>
            <Textarea data-testid="intake-concern" rows={5} className="text-[16px] rounded-xl border-[#1C4358]/20 focus-visible:ring-0 bg-white"
              value={form.main_concern} onChange={(e) => { set("main_concern", e.target.value); markStarted(); }}
              placeholder="For example: My loved one lives alone and we're worried about the stairs…" />
          </F>
          {schema.ai_helper_enabled && (
            <div>
              <button type="button" data-testid="intake-helper-btn" onClick={askHelper} disabled={helperLoading}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#9C1E22] hover:underline">
                {helperLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Not sure what to write?
              </button>
              {(helper || helperOverride) && (
                <p data-testid="intake-helper-text" className="mt-2 text-[14px] text-[#4B5563] bg-[#FBF6EC] border border-[#1C4358]/10 rounded-lg px-3 py-2">
                  {helper || helperOverride}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 3 — path */}
      {step === 3 && (
        <div data-testid="intake-step-3">
          <p className="text-[15px] text-[#4B5563] mb-3">Which of these feels closest? (You can change this anytime.)</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {situations.map((sit) => (
              <button key={sit.key} type="button" data-testid={`intake-situation-${sit.key}`}
                onClick={() => { set("situation", sit.key); markStarted(); }}
                className={`mmm-tap text-left rounded-xl border px-4 py-3.5 text-[15px] font-medium transition-colors flex items-center justify-between gap-2 ${
                  form.situation === sit.key ? "border-[#9C1E22] bg-[#9C1E22]/6 text-[#1C4358]" : "border-[#1C4358]/15 bg-white text-[#252221] hover:border-[#1C4358]/40"}`}>
                {sit.label}
                {form.situation === sit.key && <Check size={17} className="text-[#9C1E22] shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 4 — conditional */}
      {step === 4 && (
        <div className="space-y-4" data-testid="intake-step-4">
          {condQs.length === 0 ? (
            <p className="text-[15px] text-[#4B5563]">No extra questions — you're almost done. Tap Continue.</p>
          ) : condQs.map((qq) => (
            <F key={qq.key} label={qq.label}>
              {qq.type === "boolean" ? (
                <div className="flex gap-2" data-testid={`intake-q-${qq.key}`}>
                  {[["yes", "Yes"], ["no", "No"], ["not_sure", "Not sure"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setAns(qq.key, v)}
                      className={`mmm-tap rounded-full px-4 h-10 text-[14px] font-medium border transition-colors ${answers[qq.key] === v ? "border-[#9C1E22] bg-[#9C1E22]/8 text-[#1C4358]" : "border-[#1C4358]/20 bg-white text-[#4B5563]"}`}>{l}</button>
                  ))}
                </div>
              ) : qq.type === "select" ? (
                <Select value={answers[qq.key] || ""} onValueChange={(v) => setAns(qq.key, v)}>
                  <SelectTrigger data-testid={`intake-q-${qq.key}`} className={ic}><SelectValue placeholder="Choose one" /></SelectTrigger>
                  <SelectContent>{(qq.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : qq.type === "textarea" ? (
                <Textarea data-testid={`intake-q-${qq.key}`} rows={3} className="text-[16px] rounded-xl border-[#1C4358]/20 focus-visible:ring-0 bg-white" value={answers[qq.key] || ""} onChange={(e) => setAns(qq.key, e.target.value)} />
              ) : (
                <Input data-testid={`intake-q-${qq.key}`} className={ic} value={answers[qq.key] || ""} onChange={(e) => setAns(qq.key, e.target.value)} placeholder="Optional" />
              )}
            </F>
          ))}
        </div>
      )}

      {/* STEP 5 — urgency + submit */}
      {step === 5 && (
        <div data-testid="intake-step-5">
          <p className="text-[15px] text-[#4B5563] mb-3">How soon do you need help?</p>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {urgencyOptions.map((u) => (
              <button key={u.key} type="button" data-testid={`intake-urgency-${u.key}`} onClick={() => set("urgency", u.key)}
                className={`mmm-tap text-left rounded-xl border px-4 py-3 text-[15px] font-medium transition-colors flex items-center justify-between gap-2 ${
                  form.urgency === u.key ? "border-[#9C1E22] bg-[#9C1E22]/6 text-[#1C4358]" : "border-[#1C4358]/15 bg-white text-[#252221] hover:border-[#1C4358]/40"}`}>
                {u.label}
                {form.urgency === u.key && <Check size={17} className="text-[#9C1E22] shrink-0" />}
              </button>
            ))}
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none mt-5">
            <Checkbox data-testid="intake-consent" checked={form.email_consent} onCheckedChange={(v) => set("email_consent", !!v)} className="mt-1" />
            <span className="text-[14px] text-[#4B5563] leading-relaxed">It's okay to email me helpful information and next steps. There's no cost or obligation, and we do not sell your personal information.</span>
          </label>
          <p data-testid="intake-privacy-note" className="mt-3 text-[13px] text-[#4B5563] leading-relaxed">
            By submitting this form, you agree that MovingMyMama may contact you about your request. We do not
            sell your personal information. See our{" "}
            <Link to="/privacy-policy" className="underline font-medium text-[#1C4358]">Privacy Policy</Link>.
          </p>
        </div>
      )}

      {/* nav */}
      <div className="mt-6 flex items-center justify-between gap-3">
        {step > 1 ? (
          <button type="button" data-testid="intake-back" onClick={back} className="mmm-tap inline-flex items-center gap-1.5 px-5 h-12 rounded-full text-[15px] font-semibold text-[#1C4358] border border-[#1C4358]/25 hover:bg-[#1C4358]/6 transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
        ) : <span />}
        {step < 5 ? (
          <button type="button" data-testid="intake-next" onClick={next} className="mmm-tap inline-flex items-center gap-2 px-7 h-12 rounded-full text-[16px] font-semibold text-white bg-[#1C4358] hover:bg-[#163645] transition-colors">
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button type="button" data-testid="intake-submit" onClick={submit} disabled={submitting}
            className="mmm-tap inline-flex items-center gap-2 px-8 h-13 py-3.5 rounded-full text-[16px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors disabled:opacity-70">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {submitLabel}
          </button>
        )}
      </div>
    </div>
  );
};
